import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { firebaseSessionClient } from "./firebaseClient.js";
import { disableQaPush, unregisterQaNativePush } from "../notifications/qaPush.js";
import { disableNativeTestPush, unregisterNativeTestPush } from "../notifications/nativePushTest.js";

const AuthContext = createContext(null);

export function AuthProvider({ children, client = firebaseSessionClient }) {
  const [session, setSession] = useState({ status: "loading", user: null, error: null });

  useEffect(() => {
    let previousUid = null;
    return client.subscribe((user, error) => {
        if (previousUid && previousUid !== user?.uid) {
          void unregisterQaNativePush();
          void unregisterNativeTestPush();
        }
        previousUid = user?.uid || null;
        if (error) {
          setSession({ status: "error", user: null, error });
          return;
        }
        setSession({
          status: user ? "authenticated" : "anonymous",
          user,
          error: null,
        });
      });
  }, [client]);

  const sendEmailLink = useCallback(
    (email, intent = "login") => (
      intent === "link" ? client.sendEmailLink(email, "link") : client.sendEmailLink(email)
    ),
    [client],
  );
  const isEmailLink = useCallback((url) => client.isEmailLink(url), [client]);
  const finishEmailLink = useCallback(
    (email, url) => client.finishEmailLink(email, url),
    [client],
  );
  const linkEmail = useCallback(
    (email, url) => client.linkEmail(email, url),
    [client],
  );
  const signInWithCustomToken = useCallback(
    (customToken) => client.signInWithCustomToken(customToken),
    [client],
  );
  const signInAsGuest = useCallback(() => client.signInAsGuest(), [client]);
  const registerWithPassword = useCallback(
    (email, password) => client.registerWithPassword(email, password),
    [client],
  );
  const linkPassword = useCallback(
    (email, password) => client.linkPassword(email, password),
    [client],
  );
  const resendEmailVerification = useCallback(
    () => client.resendEmailVerification(), [client],
  );
  const refreshEmailVerification = useCallback(
    () => client.refreshEmailVerification(), [client],
  );
  const sendPasswordReset = useCallback(
    (email) => client.sendPasswordReset(email), [client],
  );
  const signInWithPassword = useCallback(
    (email, password) => client.signInWithPassword(email, password),
    [client],
  );
  const signOut = useCallback(async () => {
    const idToken = await client.getIdToken().catch(() => null);
    await disableQaPush(idToken).catch(() => {});
    await disableNativeTestPush(idToken).catch(() => {});
    return client.signOut();
  }, [client]);
  const getIdToken = useCallback((forceRefresh = false) => client.getIdToken(forceRefresh), [client]);

  const value = useMemo(
    () => ({
      ...session,
      sendEmailLink,
      isEmailLink,
      finishEmailLink,
      linkEmail,
      signInAsGuest,
      registerWithPassword,
      linkPassword,
      resendEmailVerification,
      refreshEmailVerification,
      sendPasswordReset,
      signInWithCustomToken,
      signInWithPassword,
      signOut,
      getIdToken,
    }),
    [finishEmailLink, getIdToken, isEmailLink, linkEmail, linkPassword, refreshEmailVerification, registerWithPassword, resendEmailVerification, sendEmailLink, sendPasswordReset, session, signInAsGuest, signInWithCustomToken, signInWithPassword, signOut],
  );

  return <AuthContext.Provider value={value}>
    {session.error?.code === "axon/saved-session-recovery-required" ? (
      <main className="auth-page">
        <section className="auth-statement" role="alert">
          <span className="auth-head__brand">AXONLEADS</span>
          <h1>Восстанавливаем ваш аккаунт</h1>
          <p>{session.error.message}</p>
          <p>Сохранённый вход остаётся на устройстве. Повторное подключение продолжит работу с вашим аккаунтом.</p>
          <button type="button" onClick={() => window.location.reload()}>Повторить подключение</button>
        </section>
      </main>
    ) : children}
  </AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
